import { Flex, Group, Select, Stack } from '@mantine/core';
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useAgentAdoption } from '../../../api/agent-miner-client/agent-miner-client.hooks';
import { ChartBox } from '../../../components/chart-styles/chart-styles';
import { FilterPanel } from '../../../components/filters/filter-panel';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { inkLight } from '../../../styles/design-tokens';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import type { TimeAllocationType } from '../../../api/work-periods-client/work-periods-client.type';
import { ADOPTION_TAB_LOCAL_PARAMS } from '../agent-miner.constants';
import { MODEL_COMPARISON_ALLOCATION_TYPES } from '../model-comparison/model-comparison.helpers';
import { useModelComparisonAllocationType } from '../model-comparison/model-comparison.hooks';
import { ALLOCATION_TYPE_LABELS } from '../model-comparison/model-comparison.type';
import { toDateOrNull, toIsoDateOrNull } from '../sessions-list/filters/session-filters.helpers';
import {
  DATE_OPTIONS,
  type DateRangePreset,
  RESET_KEYS,
  type SessionsFiltersValue,
} from '../sessions-list/filters/session-filters.type';
import { AdoptionAttributionView } from './adoption-attribution-view';
import { AdoptionKpiStrip } from './adoption-kpi-strip';
import { AdoptionTeamsTable } from './adoption-teams-table';
import { AdoptionTrendChart } from './adoption-trend-chart';
import { isAdoptionEmpty } from './adoption.helpers';
import { useAdoptionParams, useAdoptionTeamFilter, useAdoptionView } from './adoption.hooks';

const DEFAULT_DATE_PRESET: DateRangePreset = '30d';

// Adoption is window-scoped, so "All time" isn't offered on this tab.
const ADOPTION_DATE_OPTIONS = DATE_OPTIONS.filter((option) => option.value !== 'all');

/**
 * Overview slot for the Adoption tab: the adoption KPI strip. Shares its
 * query (and cache entry) with AdoptionMain via the identical query key.
 *
 * @returns {JSX.Element} The KPI strip bound to the adoption summary
 */
export const AdoptionOverview = () => {
  const params = useAdoptionParams();
  const { data, query } = useAgentAdoption(params);

  return <AdoptionKpiStrip summary={data?.summary} isLoading={query.isPending && !data} />;
};

type AdoptionFiltersProps = {
  value: SessionsFiltersValue;
  setDateRange: (value: DateRangePreset) => void;
  setCustomRange: (start: string | null, end: string | null) => void;
  onClose: () => void;
};

/**
 * Side filter panel for the Adoption tab: a trends/teams/attribution view
 * selector at the top, the shared date-window and allocation controls (same
 * URL params as Model Comparison, so the range carries across tabs), and a
 * multi-select team filter fed from the boot-hydrated global teams list. The
 * trend-measure select lives in the chart's own header, not here.
 *
 * @param {AdoptionFiltersProps} props - Component props
 * @returns {JSX.Element} The filter panel content
 */
export const AdoptionFilters = ({ value, setDateRange, setCustomRange, onClose }: AdoptionFiltersProps) => {
  const { t } = useTranslation();
  const { view, setView } = useAdoptionView();
  const { teamIds, setTeamIds } = useAdoptionTeamFilter();
  const { effectiveAllocationType, setAllocationType } = useModelComparisonAllocationType();
  const teams = useGlobalStore((state) => state.teams);
  // Own search-params instance for Reset — the individual URL-backed setters
  // each snapshot searchParams at call time, so composing them in one handler
  // races off the same stale snapshot (see ModelComparisonFilters).
  const [searchParams, setSearchParams] = useSearchParams();

  const handleResetFilters = () => {
    const next = new URLSearchParams(searchParams);
    for (const key of RESET_KEYS) next.delete(key);
    for (const key of ADOPTION_TAB_LOCAL_PARAMS) next.delete(key);
    next.delete('allocation');
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const dateSection = (
    <Stack gap={16}>
      <Group gap="xs">
        <Icon name="schedule" size={16} color={inkLight} />
        <Text weight="medium" size="small">
          {t('agent_miner.started')}
        </Text>
      </Group>
      <Select
        data={ADOPTION_DATE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
        // A stale "All time" from the shared Sessions param shows as the default.
        value={value.dateRange === 'all' ? DEFAULT_DATE_PRESET : value.dateRange}
        onChange={(next) => setDateRange((next ?? DEFAULT_DATE_PRESET) as DateRangePreset)}
        size="sm"
        radius="xl"
        allowDeselect={false}
      />
      {value.dateRange === 'custom' && (
        <DatePickerInput
          type="range"
          placeholder={t('agent_miner.adoption.pick_dates')}
          value={[toDateOrNull(value.customStart), toDateOrNull(value.customEnd)]}
          onChange={(range) => {
            const [start, end] = range as DatesRangeValue;
            setCustomRange(toIsoDateOrNull(start), toIsoDateOrNull(end));
          }}
          valueFormatter={({ date }) => {
            if (!Array.isArray(date)) return '';
            const [start, end] = date;
            if (!start || !end) return '';
            return `${dayjs(start).format('MM/DD/YYYY')} – ${dayjs(end).format('MM/DD/YYYY')}`;
          }}
          maxDate={new Date()}
          size="sm"
          radius="xl"
          clearable
        />
      )}
      <Divider />
      <Group gap="xs">
        <Icon name="date_range" size={16} color={inkLight} />
        <Text weight="medium" size="small">
          {t('agent_miner.model_comparison.allocation_type')}
        </Text>
      </Group>
      <Select
        aria-label={t('agent_miner.model_comparison.allocation_type')}
        data={MODEL_COMPARISON_ALLOCATION_TYPES.map((type) => ({
          value: type,
          label: ALLOCATION_TYPE_LABELS[type],
        }))}
        value={effectiveAllocationType}
        onChange={(next) => {
          if (next) setAllocationType(next as TimeAllocationType);
        }}
        size="sm"
        radius="xl"
        allowDeselect={false}
      />
    </Stack>
  );

  return (
    <FilterPanel<typeof view>
      selectedOption={view}
      options={[
        {
          value: 'trends',
          label: t('agent_miner.adoption.view_trends'),
          leftSection: <Icon name="show_chart" size={16} color={inkLight} />,
        },
        {
          value: 'teams',
          label: t('agent_miner.adoption.view_teams'),
          leftSection: <Icon name="groups" size={16} color={inkLight} />,
        },
        {
          value: 'attribution',
          label: t('agent_miner.adoption.view_attribution'),
          leftSection: <Icon name="account_tree" size={16} color={inkLight} />,
        },
      ]}
      onOptionChange={setView}
      headerSection={dateSection}
      filterGroups={[
        {
          name: t('agent_miner.adoption.team'),
          icon: 'groups',
          options: teams.map((team) => ({ value: team.id, label: team.name })),
          selectedValues: teamIds,
          onSelectionChange: setTeamIds,
          // `optional` is load-bearing: without it FilterPanel auto-selects
          // EVERY team on mount, writing all ids into ad_teams and splitting
          // the query cache. Empty selection = "all teams".
          optional: true,
          multiSelect: true,
          searchable: true,
        },
      ]}
      onResetFilters={handleResetFilters}
      onClose={onClose}
    />
  );
};

/**
 * Main-section content for the Adoption tab: the adoption trend chart, the
 * Teams scorecard, or the attribution breakdown, switched via the filter
 * panel's view selector. Owns the loading, error, and empty states.
 *
 * @returns {JSX.Element} The chart, table, or attribution view filling the main section
 */
export const AdoptionMain = () => {
  const { t } = useTranslation();
  const params = useAdoptionParams();
  const { view } = useAdoptionView();
  const { teamIds } = useAdoptionTeamFilter();
  const { data, query } = useAgentAdoption(params);

  if (query.isError) {
    return (
      <CenteredState>
        <Icon name="error" color={inkLight} size={48} />
        <Text size="regular" color={inkLight}>
          {t('agent_miner.adoption.error')}
        </Text>
      </CenteredState>
    );
  }

  if (query.isPending || !data) {
    return (
      <CenteredState>
        <Text size="regular" color={inkLight}>
          {t('agent_miner.adoption.loading')}
        </Text>
      </CenteredState>
    );
  }

  if (isAdoptionEmpty(data)) {
    return (
      <CenteredState>
        <Icon name="insights" color={inkLight} size={48} />
        <Text size="regular" color={inkLight}>
          {teamIds.length > 0 ? t('agent_miner.adoption.empty_filtered') : t('agent_miner.adoption.empty')}
        </Text>
      </CenteredState>
    );
  }

  return (
    <Flex direction="column" gap={16} h="100%" data-testid="adoption-main">
      {view === 'trends' ? (
        <ChartBox style={{ flex: 1, minHeight: 0 }}>
          <AdoptionTrendChart response={data} />
        </ChartBox>
      ) : view === 'teams' ? (
        <Stack gap={16}>
          <Stack gap={4}>
            <Text weight="bold" size="medium">
              {t('agent_miner.adoption.teams_title')}
            </Text>
            <Text size="small" color={inkLight}>
              {t('agent_miner.adoption.teams_subtitle')}
            </Text>
          </Stack>
          <AdoptionTeamsTable response={data} />
        </Stack>
      ) : (
        <AdoptionAttributionView response={data} />
      )}
    </Flex>
  );
};

/**
 * Centered icon-and-message block used for the loading, error, and empty
 * states.
 *
 * @param {{ children: ReactNode }} props - Component props
 * @returns {JSX.Element} The centered state block
 */
const CenteredState = ({ children }: { children: ReactNode }) => (
  <Flex direction="column" align="center" justify="center" gap={12} mih={280} h="100%">
    {children}
  </Flex>
);
